// src/themes/modern/restaurant/components/RestaurantLocation.tsx
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaDirections,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaPhone,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import type { Branch } from "~/types/new_types";
import { getBranchAddress } from "~/utils/brandHelper";
import i18n from "~/i18n/config";
import LocationMap from "~/components/GoogleMap/LocationMap";
import RestaurantLocationRating from "./RestaurantLocationRating";
interface RestaurantLocationProps {
  restaurant: Branch;
}

const RestaurantLocation: React.FC<RestaurantLocationProps> = ({
  restaurant,
}) => {

  const { t } = useTranslation();

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: restaurant.theme.cardColor,
          borderRadius: restaurant.theme.borderRadius,
          boxShadow: `0 10px 30px -10px ${restaurant.theme.primaryColor}20`,
        }}
      >
        <div className="p-6">
          <h2
            className="text-2xl font-bold mb-4"
            style={{ color: restaurant.theme.primaryColor }}
          >
            {t("restaurant.location.title")}
          </h2>
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div
              className="flex-grow space-y-4"
              style={{ color: restaurant.theme.textColor }}
            >
              <div className="flex items-start gap-3">
                <FaMapMarkerAlt
                  className="mt-1"
                  style={{ color: restaurant.theme.primaryColor }}
                />
                <address className="not-italic whitespace-pre-wrap">
                  <bdi>{getBranchAddress(restaurant, i18n.language)}</bdi>
                </address>
              </div>
              <div className="flex items-center gap-3">
                <FaPhone style={{ color: restaurant.theme.primaryColor }} />
                <a href={`tel:${restaurant.phone}`} className="hover:underline">
                  <bdi>{restaurant.phone}</bdi>
                </a>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Link
                to={`https://www.google.com/maps/dir/?api=1&destination=${restaurant.location?.coordinates}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg cursor-pointer"
                style={{
                  backgroundColor: restaurant.theme.primaryColor,
                  color: "#fff",
                  borderRadius: restaurant.theme.borderRadius,
                }}
              >
                <FaDirections />
                <span>{t("restaurant.location.getDirections")}</span>
              </Link>

              <Link
                to={
                  restaurant.location?.google_maps_link ??
                  `https://www.google.com/maps?q=${restaurant.location?.coordinates}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg cursor-pointer"
                style={{
                  backgroundColor: `${restaurant.theme.primaryColor}20`,
                  color: restaurant.theme.textColor,
                  borderRadius: restaurant.theme.borderRadius,
                }}
              >
                <FaExternalLinkAlt />
                <span>{t("restaurant.location.viewOnGoogleMaps")}</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Map */}
        <div
          className="relative overflow-hidden"
          style={{
            borderBottomLeftRadius: restaurant.theme.borderRadius,
            borderBottomRightRadius: restaurant.theme.borderRadius,
          }}
        >
          <LocationMap
            restaurantLocation={restaurant.location}
            restaurantName={restaurant.name}
            restaurantLogo={restaurant.logo}
            placeId={restaurant.location?.place_id}
            showReviews={false}
            theme={{
              primaryColor: restaurant.theme.primaryColor,
              textColor: restaurant.theme.textColor,
              backgroundColor: restaurant.theme.backgroundColor,
            }}
          />
        </div>


        {restaurant.location?.coordinates && (

          <RestaurantLocationRating restaurant={restaurant} />

        )
        }
      </motion.div>
    </div>
  );
};

export default RestaurantLocation;
